import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { MessageSquare } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { PageShell } from "@/components/nav";
import { isFirebaseConfigured } from "@/lib/firebase-config";
import { subscribeToUserRooms } from "@/lib/firebaseChat";
import type { ChatRoom } from "@/types/api";
import { formatDateTime } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/chats/")({
  component: ChatsList,
});

function ChatsList() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState<ChatRoom[] | null>(null);

  useEffect(() => {
    if (!user || !isFirebaseConfigured()) {
      setRooms([]);
      return;
    }
    try {
      // The error arm matters: a denied read otherwise leaves this on its
      // loading skeletons forever, which reads as a slow network.
      return subscribeToUserRooms(user.id, setRooms, () => setRooms([]));
    } catch {
      setRooms([]);
    }
  }, [user]);

  return (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-3xl font-extrabold tracking-tight text-ink">Messages</h1>
        {/* Both sides land here. A host reading this page is looking at
            conversations with their guests, and may also be a guest somewhere
            else — so the copy names neither role. */}
        <p className="mt-1 text-sm text-ink-muted">One conversation per booking.</p>

        {!isFirebaseConfigured() && (
          <div className="mt-6 rounded-2xl border border-accent/40 bg-accent/20 p-4 text-sm text-ink">
            Chat is not configured yet. Set the <code className="font-mono">VITE_FIREBASE_*</code>{" "}
            env vars.
          </div>
        )}

        {rooms === null && (
          <div className="mt-6 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-20 animate-pulse rounded-2xl border border-border brand-skeleton"
              />
            ))}
          </div>
        )}

        {rooms && rooms.length === 0 && (
          <div className="mt-10 rounded-3xl border border-border bg-card p-10 text-center">
            <MessageSquare className="mx-auto h-8 w-8 text-ink-subtle" />
            <p className="mt-3 text-lg font-bold text-ink">No conversations yet</p>
            <p className="mt-1 text-sm text-ink-muted">
              A conversation opens with every booking — the stays you book, and the ones booked with
              you.
            </p>
            <Link
              to="/bookings"
              className="mt-5 inline-block rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"
            >
              View my bookings
            </Link>
          </div>
        )}

        <ul className="mt-6 space-y-2">
          {rooms?.map((r) => {
            // Per room, not per account: someone who hosts can still be the
            // guest in another host's conversation.
            const unread = r.guestId === user?.id ? r.guestUnread : r.hostUnread;
            return (
              <li key={r.id}>
                <Link
                  to="/chats/$bookingId"
                  params={{ bookingId: r.bookingId }}
                  className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition hover:bg-muted/40"
                >
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-primary/15 text-sm font-extrabold text-primary">
                    {r.bookingId.slice(-2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-ink">
                      Booking {r.bookingId.slice(-6)}
                    </p>
                    <p className="truncate text-xs text-ink-muted">
                      {r.lastMessage || "No messages yet"}
                    </p>
                  </div>
                  <div className="text-right">
                    {r.lastMessageAt > 0 && (
                      <p className="text-[11px] text-ink-subtle">
                        {formatDateTime(r.lastMessageAt)}
                      </p>
                    )}
                    {unread > 0 && (
                      <span className="mt-1 inline-block min-w-5 rounded-full bg-accent px-1.5 text-center text-[11px] font-bold text-accent-foreground">
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </PageShell>
  );
}
